import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Prohibit } from 'phosphor-react';
import { MOTIVOS_CANCELAMENTO } from '../../shared/constants';
import { Banner, C, IconTile, NButton } from '../../shared/ui';

interface CancelarModalProps {
  isOpen: boolean;
  onClose: () => void;
  numero?: string | null;
  isLoading: boolean;
  onConfirmar: (payload: { motivo: string; justificativa: string }) => void;
}

const MIN_JUSTIFICATIVA = 15;

// Modal só para o que é irreversível: o cancelamento aceito pela Sefin não se desfaz.
export function CancelarModal({
  isOpen,
  onClose,
  numero,
  isLoading,
  onConfirmar,
}: CancelarModalProps) {
  const [motivo, setMotivo] = useState('1');
  const [justificativa, setJustificativa] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMotivo('1');
      setJustificativa('');
    }
  }, [isOpen]);

  const tamanho = justificativa.trim().length;
  const valido = !!motivo && tamanho >= MIN_JUSTIFICATIVA;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="rgba(15, 23, 42, 0.22)" backdropFilter="blur(6px)" />
      <ModalContent
        borderRadius="24px"
        bg="rgba(255, 255, 255, 0.94)"
        boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 30px 80px -20px rgba(15, 23, 42, 0.35)"
        p={2}
      >
        <ModalBody pt={5}>
          <Flex gap="14px" align="flex-start">
            <IconTile icon={Prohibit} tone="danger" size={44} iconSize={21} />
            <Box>
              <Text as="h2" fontSize="17px" fontWeight={700} color={C.ink}>
                Cancelar NFS-e nº {numero}?
              </Text>
              <Text fontSize="13px" color={C.muted} mt={1}>
                O pedido vai para a Sefin assinado com o certificado do emitente. Depois de aceito,
                não dá para desfazer.
              </Text>
            </Box>
          </Flex>
          <Flex direction="column" gap="14px" mt={5}>
            <FormControl isRequired>
              <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="motivo">
                Motivo
              </FormLabel>
              <Select
                id="motivo"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                borderRadius="10px"
                bg="white"
              >
                {MOTIVOS_CANCELAMENTO.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="just">
                Justificativa
              </FormLabel>
              <Textarea
                id="just"
                rows={3}
                placeholder="Ex.: valor do serviço lançado em duplicidade"
                value={justificativa}
                onChange={e => setJustificativa(e.target.value)}
                borderRadius="10px"
                bg="white"
              />
              <FormHelperText
                fontSize="11.5px"
                color={tamanho >= MIN_JUSTIFICATIVA ? C.success : C.subtle}
              >
                {tamanho}/{MIN_JUSTIFICATIVA} caracteres no mínimo
              </FormHelperText>
            </FormControl>
            <Banner tone="info" compact>
              Para corrigir dados, prefira <b>Substituir</b>: a nova nota já vem preenchida e aponta
              para esta.
            </Banner>
          </Flex>
        </ModalBody>
        <ModalFooter gap={2}>
          <NButton variant="ghost" onClick={onClose}>
            Voltar
          </NButton>
          <NButton
            variant="danger"
            icon={Prohibit}
            isDisabled={!valido}
            isLoading={isLoading}
            onClick={() => onConfirmar({ motivo, justificativa: justificativa.trim() })}
          >
            Confirmar cancelamento
          </NButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
